import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getKitColorName } from '../constants/kitColorPalette';

const OrderFormModal = ({ open, onClose, selectedKit, selectedColors }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    team_name: '',
    total_kits: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const kitOptions = Array.from({ length: 19 }, (_, i) => i + 12); // 12 to 30

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }
    
    if (!formData.team_name.trim()) {
      newErrors.team_name = 'Team name is required';
    }
    
    if (!formData.total_kits) {
      newErrors.total_kits = 'Number of kits is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare order data with all selected colors and kit details
      const orderData = {
        // Customer details
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        team_name: formData.team_name,
        total_kits: formData.total_kits,
        
        // Kit customization details
        kit_id: selectedKit,
        kit_design: 'Football Kit Design',
        
        // Selected colors with names
        colors: {
          color1: {
            hex: selectedColors.color1,
            name: getColorName(selectedColors.color1),
            usage: 'Primary Jersey Color'
          },
          color2: {
            hex: selectedColors.color2,
            name: getColorName(selectedColors.color2),
            usage: 'Secondary Jersey Color'
          },
          color3: {
            hex: selectedColors.color3,
            name: getColorName(selectedColors.color3),
            usage: 'Collar Color'
          },
          color4: {
            hex: selectedColors.color4,
            name: getColorName(selectedColors.color4),
            usage: 'Logo Color'
          },
          color5: {
            hex: selectedColors.color5,
            name: getColorName(selectedColors.color5),
            usage: 'Number Color'
          }
        },
        
        // Additional metadata
        timestamp: new Date().toISOString(),
        source: 'Football Design Page'
      };
      
      // Send to backend API
      const apiUrl = process.env.REACT_APP_API_URL || "/api";
      const response = await fetch(`${apiUrl}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            team_name: '',
            total_kits: ''
          });
          setSubmitSuccess(false);
          onClose();
        }, 3000);
      } else {
        throw new Error('Failed to submit order');
      }
      
    } catch (error) {
      console.error('Error submitting order:', error);
      setErrors({ submit: 'Failed to submit order. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getColorName = (hexColor) => getKitColorName(hexColor, 'Custom Color');

  if (submitSuccess) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: 'center',
            p: 4
          }
        }}
      >
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ color: '#4caf50', mb: 2 }}>
              ✓
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Thank You!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We just sent you quotation email. Check out your inbox or junk folder.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              One of our friendly team members will be in touch shortly.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              If you would like to speak to someone immediately feel free to call
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              <a href="tel:08006351020" style={{ color: 'inherit', textDecoration: 'none' }}>
                0800 635 1020
              </a>
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ backgroundColor: '#000', color: '#fff', p: 1, textAlign: 'center', width: '100%' }}>
          ORDER DETAILS
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ height: 400, overflow: 'auto' }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Team Name"
            value={formData.team_name}
            onChange={(e) => handleInputChange('team_name', e.target.value)}
            error={!!errors.team_name}
            helperText={errors.team_name}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal" error={!!errors.total_kits}>
            <InputLabel>No. of Kits</InputLabel>
            <Select
              value={formData.total_kits}
              onChange={(e) => handleInputChange('total_kits', e.target.value)}
              label="No. of Kits"
            >
              {kitOptions.map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
            {errors.total_kits && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.total_kits}
              </Typography>
            )}
          </FormControl>

          {/* Hidden fields for kit data */}
          <input type="hidden" name="kit_id" value={selectedKit} />
          <input type="hidden" name="colors" value={JSON.stringify(selectedColors)} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ border: '2px solid #000', color: '#000' }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ backgroundColor: '#000', color: '#fff' }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderFormModal;
